import { useCallback, useEffect, useState } from 'react';
import { ApiError, getSessions, signOutOthers, signOutSession, type DeviceSession } from '../api';
import { devicesSummary, lastUsedText, shortDate } from '../devices-text';

/**
 * Settings → Account → Devices (PR B1): every browser signed in to this account.
 * Signing out another device also changes the phone line's SIP password (the
 * signed-out browser had it), so this browser's phone quietly reloads it; a call
 * in progress goes on.
 */
export function Devices() {
  const [list, setList] = useState<DeviceSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  // folded by default, like Password → Change: the row says how many are signed in
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await getSessions();
      setList(r.sessions);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Couldn’t load your devices.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function signOut(d: DeviceSession) {
    setBusy(d.id);
    setMessage(null);
    try {
      await signOutSession(d.id);
      setMessage({ tone: 'ok', text: `${d.device} was signed out.` });
    } catch (err) {
      setMessage({ tone: 'bad', text: err instanceof ApiError ? err.message : 'Couldn’t sign out that device. Try again.' });
    } finally {
      setBusy(null);
      void load();
    }
  }

  async function signOutAll() {
    setBusy('all');
    setMessage(null);
    try {
      const r = await signOutOthers();
      setMessage({ tone: 'ok', text: r.signedOut === 1 ? '1 other device was signed out.' : `${r.signedOut} other devices were signed out.` });
    } catch (err) {
      setMessage({ tone: 'bad', text: err instanceof ApiError ? err.message : 'Couldn’t sign out the other devices. Try again.' });
    } finally {
      setBusy(null);
      setConfirmAll(false);
      void load();
    }
  }

  const now = Date.now();
  const others = (list ?? []).filter((d) => !d.current);
  const summary = devicesSummary(list, error !== null);

  return (
    <div className="setting setting-stack">
      <div className="setting-head">
        <div className="setting-text">
          <span className="setting-name" id="devices-label">
            Devices
          </span>
          <span className="setting-help">{summary}</span>
        </div>
        <button
          className="btn btn-small"
          type="button"
          aria-expanded={open}
          aria-controls="devices-list"
          onClick={() => {
            setOpen((v) => !v);
            setConfirmAll(false);
            if (!open) void load();
          }}
        >
          {open ? 'Hide' : 'Manage'}
        </button>
      </div>
      {open && (
        <div id="devices-list" className="devices-body" role="region" aria-labelledby="devices-label">
          <p className="setting-note">Browsers signed in to your account. Sign out any you don’t recognise.</p>
          {error && (
            <p className="setting-msg setting-msg-bad" role="alert">
              {error}
            </p>
          )}
          {list === null && !error && <p className="setting-help">Loading…</p>}
          {list && (
            <ul className="device-list">
              {list.map((d) => (
                <li key={d.id} className="device">
                  <div className="device-text">
                    <span className="device-name">
                      {d.device}
                      {d.current && <span className="device-this">This device</span>}
                    </span>
                    <span className="device-meta">
                      {d.current ? 'Active now' : lastUsedText(d.lastUsedAt, now)}
                      {d.ip ? ` · ${d.ip}` : ''} · signed in {shortDate(Date.parse(d.createdAt), now)}
                    </span>
                  </div>
                  {!d.current && (
                    <button className="btn btn-small" type="button" disabled={busy !== null} onClick={() => void signOut(d)}>
                      {busy === d.id ? 'Signing out…' : 'Sign out'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {others.length > 1 &&
            (confirmAll ? (
              <div className="device-confirm">
                <span>Sign out all {others.length} other devices?</span>
                <span className="password-actions">
                  <button className="btn btn-quiet btn-small" type="button" disabled={busy !== null} onClick={() => setConfirmAll(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary btn-small" type="button" disabled={busy !== null} onClick={() => void signOutAll()}>
                    {busy === 'all' ? 'Signing out…' : 'Sign out all'}
                  </button>
                </span>
              </div>
            ) : (
              <button className="btn btn-small device-all" type="button" disabled={busy !== null} onClick={() => setConfirmAll(true)}>
                Sign out all other devices
              </button>
            ))}
          {message && (
            <p className={`setting-msg setting-msg-${message.tone}`} role={message.tone === 'bad' ? 'alert' : 'status'}>
              {message.text}
            </p>
          )}
          {list && list.length > 0 && others.length === 0 && <p className="setting-note">You’re not signed in anywhere else.</p>}
        </div>
      )}
      {!open && message && (
        <p className={`setting-msg setting-msg-${message.tone}`} role={message.tone === 'bad' ? 'alert' : 'status'}>
          {message.text}
        </p>
      )}
    </div>
  );
}
